import * as React from 'react';

import { partitionCardColumns } from './lib/partition-card-columns.js';
import { rootFontSize } from './lib/root-font-size.js';
import { cn } from './lib/utils.js';

/** The width, in rem, from which each column count applies: 45rem, then Tailwind's @6xl and @7xl container sizes. */
const COLUMN_MIN_WIDTH_REM = { 2: 45, 3: 72, 4: 80 } as const;

const GAP_REM = 1;

type DimeCardColumnsProps = Readonly<
  React.ComponentProps<'div'> & {
    maxColumns?: 1 | 2 | 3 | 4;
  }
>;

type FrozenLayout = { signature: string; assignment: number[] };

function columnCountForWidth(width: number | null, remPx: number, maxColumns: number) {
  if (width === null) return 1;
  let count = 1;
  for (const [columns, minRem] of Object.entries(COLUMN_MIN_WIDTH_REM)) {
    if (Number(columns) <= maxColumns && width >= minRem * remPx) count = Number(columns);
  }
  return count;
}

function useContainerWidth(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    setWidth(container.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);
  return width;
}

function useCardHeights(cardRefs: React.RefObject<Map<string, HTMLDivElement>>, signature: string) {
  const [heights, setHeights] = React.useState<Record<string, number>>({});
  React.useLayoutEffect(() => {
    const measure = () =>
      setHeights((previous) => {
        const next = Object.fromEntries(
          [...cardRefs.current].map(([key, element]) => [key, element.offsetHeight]),
        );
        const same =
          Object.keys(next).length === Object.keys(previous).length &&
          Object.entries(next).every(([key, height]) => previous[key] === height);
        return same ? previous : next;
      });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    for (const element of cardRefs.current.values()) observer.observe(element);
    return () => observer.disconnect();
  }, [cardRefs, signature]);
  return heights;
}

export function DimeCardColumns({
  maxColumns = 4,
  className,
  style,
  children,
  onPointerDownCapture,
  onKeyDownCapture,
  onFocusCapture,
  ...props
}: DimeCardColumnsProps) {
  const cards = React.Children.toArray(children);
  const keys = cards.map((card, index) =>
    React.isValidElement(card) && card.key !== null ? String(card.key) : String(index),
  );
  const keysSignature = keys.join('\n');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef(new Map<string, HTMLDivElement>());
  const [remPx] = React.useState(rootFontSize);
  const gapPx = GAP_REM * remPx;
  const width = useContainerWidth(containerRef);
  const columnCount = columnCountForWidth(width, remPx, maxColumns);
  const heights = useCardHeights(cardRefs, keysSignature);
  const slots = keys.map((key) => Math.max(1, Math.round((heights[key] ?? 0) + gapPx)));

  // Columns follow the cards' heights while they load, then hold still once the user starts using them.
  const layoutSignature = `${columnCount}:${keysSignature}`;
  const balanced = partitionCardColumns(slots, columnCount);
  const [frozen, setFrozen] = React.useState<FrozenLayout | null>(null);
  if (frozen && frozen.signature !== layoutSignature) {
    setFrozen({ signature: layoutSignature, assignment: balanced });
  }
  const assignment = frozen?.signature === layoutSignature ? frozen.assignment : balanced;
  const freeze = () => {
    if (!frozen) setFrozen({ signature: layoutSignature, assignment });
  };

  const columnTops = new Array<number>(columnCount).fill(1);
  const rowStarts = slots.map((slot, index) => {
    const column = assignment[index] ?? 0;
    const start = columnTops[column] ?? 1;
    columnTops[column] = start + slot;
    return start;
  });

  return (
    <div ref={containerRef}>
      <div
        className={cn('grid items-start gap-x-4', className)}
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gridAutoRows: '1px',
          ...style,
        }}
        onPointerDownCapture={(event) => {
          freeze();
          onPointerDownCapture?.(event);
        }}
        onKeyDownCapture={(event) => {
          freeze();
          onKeyDownCapture?.(event);
        }}
        onFocusCapture={(event) => {
          freeze();
          onFocusCapture?.(event);
        }}
        {...props}
      >
        {cards.map((card, index) => {
          const key = keys[index] ?? String(index);
          const column = assignment[index] ?? 0;
          return (
            <div
              key={key}
              data-column={column}
              ref={(element) => {
                if (element) cardRefs.current.set(key, element);
                else cardRefs.current.delete(key);
              }}
              style={{
                gridColumn: column + 1,
                gridRow: `${rowStarts[index]} / span ${slots[index]}`,
              }}
            >
              {card}
            </div>
          );
        })}
      </div>
    </div>
  );
}
